import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { AgentConnectorRequirementsService } from './agent-connector-requirements.service';
import { ConnectorApiService } from './connector-api.service';
import { ConnectorAutoReconnectService } from './connector-auto-reconnect.service';
import type {
  ConnectorRequirement,
  ConnectorRequirementDefinition,
} from './connector.models';
import { ConnectorOAuthService } from './connector-oauth.service';

export const CONNECTOR_STATUS_POLL_MS = 60 * 1000;

@Injectable()
export class ConnectorStateService {
  private readonly requirementResolver = inject(AgentConnectorRequirementsService);
  private readonly api = inject(ConnectorApiService);
  private readonly oauth = inject(ConnectorOAuthService);
  private readonly autoReconnect = inject(ConnectorAutoReconnectService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly definitions = signal<ConnectorRequirementDefinition[]>([]);
  private readonly requirementsValue = signal<ConnectorRequirement[]>([]);
  private readonly loadingValue = signal(false);
  private readonly checkingValue = signal(false);
  private readonly errorValue = signal<string | null>(null);
  private readonly pendingIds = signal<ReadonlySet<string>>(new Set());

  readonly requirements = this.requirementsValue.asReadonly();
  readonly isLoading = this.loadingValue.asReadonly();
  readonly isChecking = this.checkingValue.asReadonly();
  readonly errorMessage = this.errorValue.asReadonly();
  readonly missing = computed(() =>
    this.requirementsValue().filter(
      (requirement) => requirement.statusResolved && !requirement.connected
    )
  );
  readonly hasAny = computed(() => this.requirementsValue().length > 0);
  readonly isVisible = computed(
    () => this.hasAny() || this.errorValue() !== null
  );

  private activeAgentIdentifier: string | null = null;
  private generation = 0;
  private pollingId: number | undefined;
  private destroyed = false;
  private statusInFlight:
    | { generation: number; promise: Promise<boolean> }
    | undefined;
  private requirementsInFlight:
    | { generation: number; promise: Promise<void> }
    | undefined;

  private readonly onFocus = () => {
    if (this.activeAgentIdentifier) void this.refreshStatus();
  };

  private readonly onVisibilityChange = () => {
    if (
      document.visibilityState === 'visible' &&
      this.activeAgentIdentifier
    ) {
      void this.refreshStatus();
    }
  };

  constructor() {
    window.addEventListener('focus', this.onFocus);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.destroyRef.onDestroy(() => this.teardown());
  }

  setAgentIdentifier(agentIdentifier: string | null | undefined): void {
    const next = agentIdentifier?.trim() || null;
    if (next === this.activeAgentIdentifier) return;

    this.activeAgentIdentifier = next;
    this.generation += 1;
    this.stopPolling();
    this.definitions.set([]);
    this.requirementsValue.set([]);
    this.errorValue.set(null);
    this.loadingValue.set(Boolean(next));
    this.checkingValue.set(false);
    this.statusInFlight = undefined;
    this.requirementsInFlight = undefined;

    if (!next || this.destroyed) return;
    const generation = this.generation;
    this.startPolling();
    void this.loadRequirements(next, generation);
  }

  refreshStatus(autoReconnect = true): Promise<void> {
    return this.refreshStatusForGeneration(this.generation, autoReconnect);
  }

  connect(mcpServerId: string): Promise<void> {
    this.setPending(mcpServerId, true);
    this.setRequirementError(mcpServerId, null);
    return this.oauth
      .connectInPopup(mcpServerId)
      .then(() => this.refreshStatus(false))
      .catch((error) => {
        this.setRequirementError(mcpServerId, errorMessage(error));
      })
      .finally(() => this.setPending(mcpServerId, false));
  }

  disconnect(mcpServerId: string): Promise<void> {
    this.autoReconnect.snooze(mcpServerId);
    this.setPending(mcpServerId, true);
    this.setRequirementError(mcpServerId, null);
    return this.api
      .disconnectConnector(mcpServerId)
      .then(() => this.refreshStatus(false))
      .catch((error) => {
        this.setRequirementError(mcpServerId, errorMessage(error));
      })
      .finally(() => this.setPending(mcpServerId, false));
  }

  isPending(mcpServerId: string): boolean {
    return this.pendingIds().has(mcpServerId);
  }

  private loadRequirements(
    agentIdentifier: string,
    generation: number
  ): Promise<void> {
    const active = this.requirementsInFlight;
    if (active?.generation === generation) return active.promise;

    const promise = this.resolveRequirements(agentIdentifier, generation).finally(
      () => {
        if (this.requirementsInFlight?.promise === promise) {
          this.requirementsInFlight = undefined;
        }
      }
    );
    this.requirementsInFlight = { generation, promise };
    return promise;
  }

  private async resolveRequirements(
    agentIdentifier: string,
    generation: number
  ): Promise<void> {
    try {
      const definitions = await this.requirementResolver.resolve(agentIdentifier);
      if (!this.isCurrent(generation)) return;
      this.definitions.set(definitions);
      this.requirementsValue.set(
        definitions.map((definition) => ({
          ...definition,
          provider: definition.configuredProvider,
          connected: false,
          statusResolved: false,
          error: null,
        }))
      );
      this.loadingValue.set(false);
      this.errorValue.set(null);
      if (definitions.length > 0) {
        await this.refreshStatusForGeneration(generation, true);
      }
    } catch (error) {
      if (!this.isCurrent(generation)) return;
      this.loadingValue.set(false);
      this.errorValue.set(errorMessage(error));
    }
  }

  private async refreshStatusForGeneration(
    generation: number,
    shouldAutoReconnect: boolean
  ): Promise<void> {
    if (!this.isCurrent(generation)) return;
    const definitions = this.definitions();
    if (definitions.length === 0) {
      if (this.errorValue() && this.activeAgentIdentifier) {
        await this.loadRequirements(this.activeAgentIdentifier, generation);
      }
      return;
    }

    let statusFetch = this.statusInFlight;
    if (!statusFetch || statusFetch.generation !== generation) {
      const promise = this.fetchStatuses(definitions, generation).finally(() => {
        if (this.statusInFlight?.promise === promise) {
          this.statusInFlight = undefined;
        }
      });
      statusFetch = { generation, promise };
      this.statusInFlight = statusFetch;
    }
    const resolved = await statusFetch.promise;
    if (!resolved || !shouldAutoReconnect || !this.isCurrent(generation)) return;

    await this.autoReconnect.reconnectOkta(this.requirementsValue(), {
      onConnected: async () => {
        if (this.isCurrent(generation)) {
          await this.refreshStatusForGeneration(generation, false);
        }
      },
      onError: (mcpServerId, error) => {
        if (this.isCurrent(generation)) {
          this.setRequirementError(mcpServerId, errorMessage(error));
        }
      },
    });
  }

  private async fetchStatuses(
    definitions: ConnectorRequirementDefinition[],
    generation: number
  ): Promise<boolean> {
    this.checkingValue.set(true);
    try {
      const statuses = await this.api.getConnectorStatuses(
        definitions.map(({ mcpServerId }) => mcpServerId)
      );
      if (!this.isCurrent(generation)) return false;
      const byServer = new Map(
        statuses.map((status) => [status.mcp_server_id, status])
      );
      this.requirementsValue.set(
        definitions.map((definition) => {
          const status = byServer.get(definition.mcpServerId);
          return {
            ...definition,
            provider: status?.provider ?? definition.configuredProvider,
            connected: status?.connected ?? false,
            statusResolved: status !== undefined,
            error: status?.error ?? null,
          };
        })
      );
      this.errorValue.set(null);
      return true;
    } catch (error) {
      if (this.isCurrent(generation)) this.errorValue.set(errorMessage(error));
      return false;
    } finally {
      if (this.isCurrent(generation)) this.checkingValue.set(false);
    }
  }

  private startPolling(): void {
    this.pollingId = window.setInterval(() => {
      if (document.visibilityState === 'visible') void this.refreshStatus();
    }, CONNECTOR_STATUS_POLL_MS);
  }

  private stopPolling(): void {
    if (this.pollingId === undefined) return;
    window.clearInterval(this.pollingId);
    this.pollingId = undefined;
  }

  private setPending(mcpServerId: string, pending: boolean): void {
    const next = new Set(this.pendingIds());
    if (pending) next.add(mcpServerId);
    else next.delete(mcpServerId);
    this.pendingIds.set(next);
  }

  private setRequirementError(mcpServerId: string, message: string | null): void {
    this.requirementsValue.update((requirements) =>
      requirements.map((requirement) =>
        requirement.mcpServerId === mcpServerId
          ? { ...requirement, error: message }
          : requirement
      )
    );
  }

  private isCurrent(generation: number): boolean {
    return !this.destroyed && generation === this.generation;
  }

  private teardown(): void {
    this.destroyed = true;
    this.generation += 1;
    this.stopPolling();
    window.removeEventListener('focus', this.onFocus);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : 'Could not refresh connector status.';
}
